import React, { useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import '../../App.css';
import './OpportunityDetail.css';
import api from '../../utils/api.js';
import AiResultModal from '../../components/AiResultModal.js';
import ConfirmModal from '../../components/ConfirmModal.js';
import SkeletonLoader from '../../components/SkeletonLoader.js';
import PromptSelectModal from '../../components/PromptSelectModal';
import SearchableSelect from '../../components/SearchableSelect';
import BasicInfoTab from './BasicInfoTab';
import ActivitiesTab from './ActivitiesTab';
import ProposedJobsTab from './ProposedJobsTab';
import useOpportunityEdit from './useOpportunityEdit';
import useOpportunityData from './useOpportunityData';
import useOpportunityAI from './useOpportunityAI';
import CreateMeetingModal from './CreateMeetingModal';
import CreateJobModal from './CreateJobModal';
import useNavigateBack from '../../utils/useNavigateBack.js';

function OpportunityDetailPage() {
    const { opportunityId } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    // URLクエリパラメータからタブを取得（例: ?tab=proposed_jobs）
    const initialTab = searchParams.get('tab') || 'basic';
    const [activeTab, setActiveTab] = useState(initialTab);

    // タブ切り替え時にURLも更新
    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setSearchParams({ tab }, { replace: true });
    };

    // --- データ取得ロジック（カスタムフック） ---
    const {
        opportunity,
        setOpportunity,
        loading,
        setLoading,
        error,
        currentUser,
        meetings,
        meetingsPage,
        setMeetingsPage,
        meetingsTotal,
        meetingsTotalPages,
        meetingsPerPage,
        fetchMeetings,
        proposedJobs,
        jobsPage,
        setJobsPage,
        jobsTotal,
        jobsTotalPages,
        jobsPerPage,
        allProposedJobs,
        fetchAllProposedJobs,
        opportunityStages
    } = useOpportunityData(opportunityId, activeTab);

    // --- 編集用ロジック（カスタムフック） ---
    const {
        isEditing,
        editData,
        editLoading,
        editError,
        handleEditDoubleClick,
        handleEditChange,
        handleCancelEdit,
        handleSaveEdit
    } = useOpportunityEdit(opportunity, opportunityId, setOpportunity);

    // --- AI処理ロジック（カスタムフック） ---
    const {
        insightLoading,
        insightResult,
        setInsightResult,
        insightError,
        setInsightError,
        handleGenerateInsight,
        resumeReviewLoading,
        resumeReviewResult,
        setResumeReviewResult,
        resumeReviewError,
        setResumeReviewError,
        handleGenerateResumeReview,
        showResumeReviewModal,
        setShowResumeReviewModal,
        recommendationLoading,
        recommendationResult,
        setRecommendationResult,
        recommendationError,
        setRecommendationError,
        showRecommendationPromptModal,
        setShowRecommendationPromptModal,
        handleClickGenerateRecommendation,
        handleRecommendationPromptSelected,
    } = useOpportunityAI(opportunityId, fetchAllProposedJobs);

    // --- モーダル用State ---
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isJobModalOpen, setIsJobModalOpen] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const handleBack = useNavigateBack('/');

    const handleDelete = async () => {
        setLoading(true);
        try {
            const response = await api.fetch(`/opportunities/${opportunityId}`, {
                method: 'DELETE',
            });
            if (!response) return; // Redirected
            if (!response.ok) {
                throw new Error('削除に失敗しました。');
            }
            setShowDeleteConfirm(false);
            alert('案件を削除しました。');
            navigate('/opportunities');
        } catch (err) {
            alert(err.message);
        } finally {
            setLoading(false);
        }
    };

    const renderTabContent = () => {
        switch (activeTab) {
            case 'basic':
                return (
                    <BasicInfoTab
                        opportunity={opportunity}
                        isEditing={isEditing}
                        editData={editData}
                        handleEditDoubleClick={handleEditDoubleClick}
                        handleEditChange={handleEditChange}
                        opportunityId={opportunityId}
                        onOpenResumeReviewModal={() => setShowResumeReviewModal(true)}
                        resumeReviewLoading={resumeReviewLoading}
                    />
                );
            case 'activities':
                return (
                    <ActivitiesTab
                        meetings={meetings}
                        handleMeetingClick={(id) => navigate(`/activity/${id}`)}
                        handleOpenModal={() => setIsModalOpen(true)}
                        meetingsPage={meetingsPage}
                        setMeetingsPage={setMeetingsPage}
                        meetingsTotalPages={meetingsTotalPages}
                        meetingsTotal={meetingsTotal}
                        meetingsPerPage={meetingsPerPage}
                    />
                );
            case 'proposed_jobs':
                return (
                    <ProposedJobsTab
                        opportunityId={opportunityId}
                        proposedJobs={proposedJobs}
                        handleOpenJobDetail={(job) => navigate(`/proposed_job/${job.id}`)}
                        handleClickGenerateRecommendation={handleClickGenerateRecommendation}
                        recommendationLoading={recommendationLoading}
                        handleOpenJobModal={() => setIsJobModalOpen(true)}
                        jobsPage={jobsPage}
                        setJobsPage={setJobsPage}
                        jobsTotalPages={jobsTotalPages}
                        jobsTotal={jobsTotal}
                        jobsPerPage={jobsPerPage}
                    />
                );
            default:
                return null;
        }
    };

    if (loading) return <SkeletonLoader variant="detail" rows={5} />;
    if (error) return <div className="error-message">エラー: {error}</div>;
    if (!opportunity) return <div className="error-message">案件が見つかりません。</div>;

    return (
        <div className="detail-container">
            <AiResultModal
                loading={insightLoading}
                loadingMessage="AIインサイトを生成中..."
                result={insightResult}
                title="✨ AIインサイト"
                error={insightError}
                onClose={() => setInsightResult(null)}
                onErrorClose={() => setInsightError(null)}
            />

            <AiResultModal
                loading={resumeReviewLoading}
                loadingMessage="AI履歴書レビューを生成中..."
                result={resumeReviewResult}
                title="✨ 履歴書・経歴レビュー"
                error={resumeReviewError}
                onClose={() => setResumeReviewResult(null)}
                onErrorClose={() => setResumeReviewError(null)}
            />

            <ConfirmModal
                isOpen={showResumeReviewModal}
                title="AI履歴書レビューの生成"
                onConfirm={handleGenerateResumeReview}
                onCancel={() => setShowResumeReviewModal(false)}
                confirmText="生成する"
            >
                <p>求職者情報、履歴書ファイル、および直近の面談記録をもとに、AIによるレビューを生成します。よろしいですか？</p>
            </ConfirmModal>

            <AiResultModal
                loading={recommendationLoading}
                loadingMessage="AI推薦文を生成中..."
                result={recommendationResult}
                title="✨ AI推薦文"
                error={recommendationError}
                onClose={() => setRecommendationResult(null)}
                onErrorClose={() => setRecommendationError(null)}
            />

            <PromptSelectModal
                isOpen={showRecommendationPromptModal}
                onClose={() => setShowRecommendationPromptModal(false)}
                onConfirm={handleRecommendationPromptSelected}
                promptType="recommendation"
                title="AI推薦文を生成"
                message="推薦先の求人とプロンプトを選択してください"
                confirmButtonText="生成する"
                secondaryOptions={allProposedJobs.map(job => ({
                    value: job.id,
                    label: job.company_name,
                }))}
                secondaryLabel="推薦先の求人"
                secondaryDefaultLabel="汎用（求人指定なし）"
            />

                <div className="detail-header">
                    <div className="header-left">
                        <h1 className="page-title">案件詳細</h1>
                    </div>

                    <div className="header-actions">
                        <button
                            className="modal-button-confirm"
                            onClick={handleGenerateInsight}
                            disabled={insightLoading}
                            style={{ marginRight: '10px' }}
                        >
                            ✨ AIインサイト
                        </button>
                        <button className="btn-delete" onClick={() => setShowDeleteConfirm(true)} style={{ marginRight: '10px' }}>
                            削除
                        </button>
                        <button className="modal-button-cancel" onClick={handleBack}>
                            前ページに戻る
                        </button>
                    </div>
                </div>

                {/* 削除確認モーダル */}
                {showDeleteConfirm && (
                    <div className="modal-overlay">
                        <div className="modal-content">
                            <h2 className="modal-title">削除の確認</h2>
                            <p style={{ textAlign: 'center', marginBottom: '25px' }}>
                                この案件を削除してもよろしいですか？<br />
                                紐づく活動履歴もすべて削除（非表示）されます。
                            </p>
                            <div className="modal-actions">
                                <button
                                    className="modal-button-cancel"
                                    onClick={() => setShowDeleteConfirm(false)}
                                    disabled={loading}
                                >
                                    キャンセル
                                </button>
                                <button
                                    className="btn-delete"
                                    onClick={handleDelete}
                                    disabled={loading}
                                    style={{ margin: 0 }}
                                >
                                    {loading ? '処理中...' : '削除する'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                <div className="detail-card">
                    {/* タブ上部: 求職者名と状況の横並び配置 */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', flexWrap: 'wrap', gap: '20px' }}>
                        <div className="detail-item" onDoubleClick={handleEditDoubleClick} style={{ borderBottom: 'none', margin: 0, flex: '1 1 300px' }}>
                            <span className="detail-label">求職者名</span>
                            {isEditing ? (
                                <input
                                    type="text"
                                    className="card-title-input"
                                    value={editData.applicant_name}
                                    onChange={(e) => handleEditChange('applicant_name', e.target.value)}
                                    autoFocus
                                    style={{ fontSize: '1.5rem', fontWeight: 'bold', width: '300px', padding: '8px' }}
                                />
                            ) : (
                                <span className="detail-value-large" style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                                    {opportunity.applicant_name}
                                </span>
                            )}
                        </div>
                        <div className="detail-item" onDoubleClick={handleEditDoubleClick} style={{ borderBottom: 'none', margin: 0, width: '200px' }}>
                            <span className="detail-label">状況</span>
                            {isEditing ? (
                                <>
                                    <div style={{ width: '200px' }}>
                                        <SearchableSelect
                                            options={opportunityStages.map(s => ({ value: s.name, label: s.name }))}
                                            value={editData.stage_name || '面談前'}
                                            onChange={(newStageName) => {
                                                handleEditChange('stage_name', newStageName);
                                                // 選択されたステージのis_closedフラグを判定
                                                const selectedStage = opportunityStages.find(s => s.name === newStageName);
                                                const newIsClosed = selectedStage ? selectedStage.is_closed : false;
                                                handleEditChange('is_closed', newIsClosed);
                                                
                                                if (newIsClosed && !editData.close_date) {
                                                    handleEditChange('close_date', new Date().toISOString().split('T')[0]);
                                                }
                                            }}
                                            placeholder="状況を選択..."
                                            searchPlaceholder="ステージを検索..."
                                            scrollOnOpen="center"
                                        />
                                    </div>
                                     {editData.is_closed && (
                                        <div className="close-date-field">
                                            <label className="detail-label" style={{ marginBottom: '2px' }}>終了日 *</label>
                                            <input
                                                type="date"
                                                className="date-select-input"
                                                value={editData.close_date || ''}
                                                onChange={(e) => handleEditChange('close_date', e.target.value)}
                                            />
                                        </div>
                                    )}
                                </>
                            ) : (
                                <>
                                    {(() => {
                                        const label = opportunity.stage_name || (opportunity.is_closed ? '終了' : '進行中');
                                        return (
                                            <div
                                                className={`status-badge ${opportunity.is_closed ? 'closed' : 'ongoing'}`}
                                                onClick={handleEditDoubleClick}
                                                style={{ cursor: 'pointer', display: 'inline-flex' }}
                                                title={label}
                                            >
                                                <span className="status-badge-dot"></span>
                                                <span className="status-badge-label">{label}</span>
                                            </div>
                                        );
                                    })()}
                                    {opportunity.is_closed && opportunity.close_date && (
                                        <div className="close-date-display">
                                            終了日: {opportunity.close_date}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>

                    <div className="detail-tabs">
                        <button
                            className={`tab-button ${activeTab === 'basic' ? 'active' : ''}`}
                            onClick={() => handleTabChange('basic')}
                        >
                            基本情報
                        </button>
                        <button
                            className={`tab-button ${activeTab === 'activities' ? 'active' : ''}`}
                            onClick={() => handleTabChange('activities')}
                        >
                            活動履歴
                        </button>
                        <button
                            className={`tab-button ${activeTab === 'proposed_jobs' ? 'active' : ''}`}
                            onClick={() => handleTabChange('proposed_jobs')}
                        >
                            提案した求人
                        </button>
                    </div>

                <div className="tab-content">
                    {renderTabContent()}
                </div>

                {/* 編集時のアクションフッター */}
                {isEditing && (
                    <div className="edit-actions-footer">
                        {editError && <p className="modal-error-message">{editError}</p>}
                        <button
                            className="modal-button-cancel"
                            onClick={handleCancelEdit}
                            disabled={editLoading}
                        >
                            キャンセル
                        </button>
                        <button
                            className="modal-button-confirm"
                            onClick={handleSaveEdit}
                            disabled={editLoading}
                        >
                            {editLoading ? '保存中...' : '保存'}
                        </button>
                    </div>
                )}
                </div>

            {/* 新規活動追加モーダル */}
            <CreateMeetingModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                opportunityId={opportunityId}
                currentUser={currentUser}
                onSuccess={(newMeeting) => {
                    setMeetingsPage(1);
                    fetchMeetings();
                    navigate(`/activity/${newMeeting.id}`);
                }}
            />

            {/* 新規求人提案モーダル */}
            <CreateJobModal
                isOpen={isJobModalOpen}
                onClose={() => setIsJobModalOpen(false)}
                opportunityId={opportunityId}
                onSuccess={(newJob) => navigate(`/proposed_job/${newJob.id}`)}
            />
        </div>
    );
}

export default OpportunityDetailPage;
